import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronLeft,
  Settings,
  BarChart2,
  Zap,
  Play,
  RotateCcw,
  Trash2,
  Dice5,
  LayoutGrid,
  RefreshCw,
} from "lucide-react";
import Modal from "../../../Modal/Modal";
import { useNavigate } from "react-router-dom";
import "./ClanEventGameTab.css";
import io from "socket.io-client"; // 1. IMPORTED SOCKET.IO

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
const MESSAGE_TIMEOUT_MS = 5000;

const PERFECT_SQUARE_SIZES = [25, 36, 49, 64, 81, 100, 121, 144, 169, 196, 225];

const calculateTaskDistribution = (boardSize, numSnakes = 0, numLadders = 0) => {
  if (!PERFECT_SQUARE_SIZES.includes(boardSize)) {
    return {
      num_easy_tasks: 0,
      num_medium_tasks: 0,
      num_hard_tasks: 0,
      num_elite_tasks: 0,
      num_master_tasks: 0,
    };
  }

  const totalTilesForTasks = boardSize - 2 - numSnakes - numLadders;
  const safeTotal = Math.max(0, totalTilesForTasks);

  const num_master = Math.floor(safeTotal * 0.16);
  const num_elite = Math.floor(safeTotal * 0.16);
  const num_hard = Math.floor(safeTotal * 0.21);
  const num_medium = Math.floor(safeTotal * 0.22);

  const num_easy = Math.max(
    0,
    safeTotal - (num_medium + num_hard + num_elite + num_master),
  );

  return {
    num_easy_tasks: num_easy,
    num_medium_tasks: num_medium,
    num_hard_tasks: num_hard,
    num_elite_tasks: num_elite,
    num_master_tasks: num_master,
  };
};

const defaultTaskDistribution = calculateTaskDistribution(100, 5, 5);

const initialFormData = {
  board_size: 100,
  num_snakes: 5,
  num_ladders: 5,
  max_reverts_allowed: 3,
  num_easy_tasks: defaultTaskDistribution.num_easy_tasks,
  num_medium_tasks: defaultTaskDistribution.num_medium_tasks,
  num_hard_tasks: defaultTaskDistribution.num_hard_tasks,
  num_elite_tasks: defaultTaskDistribution.num_elite_tasks,
  num_master_tasks: defaultTaskDistribution.num_master_tasks,
};

const validateTaskSum = (formData) => {
  const board_size = parseInt(formData.board_size, 10);
  const num_snakes = parseInt(formData.num_snakes || "0", 10);
  const num_ladders = parseInt(formData.num_ladders || "0", 10);
  
  const num_easy_tasks = parseInt(formData.num_easy_tasks || "0", 10);
  const num_medium_tasks = parseInt(formData.num_medium_tasks || "0", 10);
  const num_hard_tasks = parseInt(formData.num_hard_tasks || "0", 10);
  const num_elite_tasks = parseInt(formData.num_elite_tasks || "0", 10);
  const num_master_tasks = parseInt(formData.num_master_tasks || "0", 10);

  if (!PERFECT_SQUARE_SIZES.includes(board_size)) {
    return "Board size must be a perfect square to validate task sum.";
  }

  const totalTilesForTasks = board_size - 2 - num_snakes - num_ladders;

  const sumOfTasks =
    num_easy_tasks +
    num_medium_tasks +
    num_hard_tasks +
    num_elite_tasks +
    num_master_tasks;

  if (
    num_easy_tasks < 0 ||
    num_medium_tasks < 0 ||
    num_hard_tasks < 0 ||
    num_elite_tasks < 0 ||
    num_master_tasks < 0
  ) {
    return "Task counts cannot be negative.";
  }

  if (sumOfTasks > totalTilesForTasks) {
    return `Total tasks (${sumOfTasks}) exceeds available task tiles (${totalTilesForTasks}). (Excludes Start, End, and ${num_snakes + num_ladders} connection points).`;
  }

  if (sumOfTasks < totalTilesForTasks) {
    return `Total tasks (${sumOfTasks}) is less than available task tiles (${totalTilesForTasks}). All task tiles must be filled.`;
  }

  return null;
};

const BoardConfigEditor = React.memo(
  ({ gameSettings, loading, onUpdate, onApiMessage }) => {
    const [configData, setConfigData] = useState({
      snakes_config: "",
      ladders_config: "",
    });
    const [localLoading, setLocalLoading] = useState(false);
    const [configError, setConfigError] = useState(null);

    useEffect(() => {
      if (gameSettings && gameSettings.board_connections) {
        const snakes = gameSettings.board_connections
          .filter((connection) => connection.type === "snake")
          .map((snake) => [snake.start, snake.end]);
        const ladders = gameSettings.board_connections
          .filter((connection) => connection.type === "ladder")
          .map((ladder) => [ladder.start, ladder.end]);
        setConfigData({
          snakes_config: JSON.stringify(snakes, null, null),
          ladders_config: JSON.stringify(ladders, null, null),
        });
      } else if (!gameSettings) {
        setConfigData({
          snakes_config: "[]",
          ladders_config: "[]",
        });
      }
    }, [gameSettings]);

    const handleConfigChange = (e) => {
      const { name, value } = e.target;
      setConfigData((prev) => ({ ...prev, [name]: value }));
      setConfigError(null);
    };

    const validateAndParse = (jsonString, type) => {
      try {
        const parsed = JSON.parse(jsonString);
        if (!Array.isArray(parsed)) {
          throw new Error(`Config must be a JSON array.`);
        }
        const isValid = parsed.every(
          (item) =>
            Array.isArray(item) &&
            item.length === 2 &&
            item.every((val) => typeof val === "number" && val > 0)
        );
        if (!isValid) {
          throw new Error(
            `Each entry must be an array of two positive numbers: [[start, end], ...].`
          );
        }
        return parsed;
      } catch (e) {
        throw new Error(`Invalid JSON format in ${type} config: ${e.message}`);
      }
    };

    const handleConfigSubmit = async (e) => {
      e.preventDefault();
      if (localLoading || loading) return;
      setLocalLoading(true);
      setConfigError(null);
      let parsedSnakes, parsedLadders;
      try {
        parsedSnakes = validateAndParse(configData.snakes_config, "Snakes");
        parsedLadders = validateAndParse(configData.ladders_config, "Ladders");

        if (!gameSettings) {
          setConfigError("Game settings not loaded. Please wait.");
          return;
        }
        const boardSize = gameSettings.board_size;

        const allConnections = [...parsedSnakes, ...parsedLadders];
        for (const [start, end] of allConnections) {
          if (
            start === 1 ||
            start === boardSize ||
            end === 1 ||
            end === boardSize
          ) {
            throw new Error(
              `Invalid connection: Cannot place connections on Start (1) or Finish (${boardSize}).`,
            );
          }
          if (start < 1 || start > boardSize || end < 1 || end > boardSize) {
            throw new Error(
              `Invalid connection: Position out of bounds (1 to ${boardSize}).`,
            );
          }
        }
      } catch (e) {
        setConfigError(e.message);
        setLocalLoading(false);
        return;
      }
      const payload = {
        snakes_config: parsedSnakes,
        ladders_config: parsedLadders,
      };
      try {
        await onUpdate(payload);
        onApiMessage("success", "Board configurations updated successfully.");
      } catch (e) {
        onApiMessage("error", "Failed to update board configuration via API.");
        setConfigError(e.message || "Unknown error during update.");
      } finally {
        setLocalLoading(false);
      }
    };

    return (
      <div className="board-config-editor">
        <h4 className="config-header">
          <Settings size={18} style={{ marginRight: "5px" }} /> Manual Board
          Configuration
        </h4>
        <p className="note-text-config">
          Edit the raw JSON arrays below to manually define the start and end
          points for all snakes and ladders. Each entry must be an array of{" "}
          <code>[start, end]</code>.
        </p>
        {configError && (
          <div className="game-global-message error form-error-message">
            {configError}
          </div>
        )}
        <form onSubmit={handleConfigSubmit}>
          <div className="form-group">
            <label className="modal-form-label" htmlFor="snakes_config">
              Snakes Config: `[[head, tail], ...]`
            </label>
            <textarea
              id="snakes_config"
              className="modal-form-field textarea-config"
              name="snakes_config"
              rows="6"
              value={configData.snakes_config}
              onChange={handleConfigChange}
              disabled={localLoading || loading}
              placeholder="Example: [[99, 50], [75, 20]]"
            />
          </div>
          <div className="form-group">
            <label className="modal-form-label" htmlFor="ladders_config">
              Ladders Config: `[[start, end], ...]`
            </label>
            <textarea
              id="ladders_config"
              className="modal-form-field textarea-config"
              name="ladders_config"
              rows="6"
              value={configData.ladders_config}
              onChange={handleConfigChange}
              disabled={localLoading || loading}
              placeholder="Example: [[5, 25], [60, 80]]"
            />
          </div>
          <button
            type="submit"
            className={`btn-secondary ${
              localLoading || loading ? "btn-disabled" : ""
            }`}
            disabled={localLoading || loading}
          >
            {localLoading ? "Saving..." : "Save Manual Configuration"}
          </button>
        </form>
      </div>
    );
  }
);

const renderGameStatus = (status) => {
  switch (status) {
    case "PENDING":
    case "READY":
      return (
        <span className="game-status-warning">{status.toUpperCase()}</span>
      );
    case "in-progress":
      return <span className="game-status-live-ongoing">IN-PROGRESS</span>;
    case "paused":
      return <span className="game-status-paused">PAUSED</span>;
    case "finished":
      return <span className="game-status-finished">FINISHED</span>;
    default:
      return <span>{status ? status.toUpperCase() : "UNKNOWN"}</span>;
  }
};

const GameStatusControls = React.memo(
  ({ status, onAction, onReset, clanId, eventId, gameId }) => {
    const navigate = useNavigate();
    const [localLoading, setLocalLoading] = useState(false);

    const isFinished = status === "finished";
    const isReady = status === "READY" || status === "PENDING";
    const isInProgress = status === "in-progress";
    const isPaused = status === "paused";

    let statusButtonText;
    let statusButtonAction;
    let statusButtonClass;

    if (isReady) {
      statusButtonText = "Start Game";
      statusButtonAction = "resume";
      statusButtonClass = "btn-primary";
    } else if (isInProgress) {
      statusButtonText = "Pause Game";
      statusButtonAction = "pause";
      statusButtonClass = "btn-warning";
    } else if (isPaused) {
      statusButtonText = "Resume Game";
      statusButtonAction = "resume";
      statusButtonClass = "btn-primary";
    } else if (isFinished) {
      statusButtonText = "Game Finished";
      statusButtonAction = null;
      statusButtonClass = "btn-success btn-disabled";
    }

    const handleAction = (action) => {
      setLocalLoading(true);
      onAction(action, () => setLocalLoading(false)); // Callback to reset loading state
    };

    const onGoToBoard = () => {
      if (clanId && eventId && gameId) {
        const gamePath = `/games/clan/${clanId}/events/${eventId}/game/${gameId}`;
        const clanManagementPath = `/clans/manage/${clanId}?tab=events`;
        navigate(gamePath, {
          state: {
            breadcrumbFrom: {
              path: clanManagementPath,
              text: "Game Management",
            },
          },
        });
      }
    };

    return (
      <div className="game-status-controls-isolated">
        <p>
          <strong>Game Status:</strong> {renderGameStatus(status)}
        </p>
        <div className="game-actions">
          {statusButtonAction && (
            <button
              className={statusButtonClass}
              onClick={() => handleAction(statusButtonAction)}
              disabled={localLoading || isFinished}
            >
              <Zap size={16} style={{ marginRight: "5px" }} />
              &nbsp;
              {localLoading ? "Processing..." : statusButtonText}
            </button>
          )}
          {!isFinished && (
            <button
              className="btn-danger-action"
              onClick={onReset} // Directly pass the reset handler
              disabled={localLoading}
            >
              <RotateCcw size={16} style={{ marginRight: "5px" }} /> Reset Game
            </button>
          )}
        </div>
        <button
          className="btn-primary"
          onClick={onGoToBoard}
          disabled={localLoading}
          style={{ width: "100%", padding: "8px", marginTop: "10px" }}
        >
          <LayoutGrid size={16} style={{ marginRight: "5px" }} /> View Live Game
          Board
        </button>
      </div>
    );
  }
);

const GameManagementView = React.memo(
  ({
    gameSettings,
    onGameAction,
    onResetAction,
    // 🔑 NEW props for admin actions
    onOpenRevertModal,
    onOpenSetPositionModal,
  }) => {
    const teamStates = gameSettings.team_states || [];
    const totalTurns = teamStates.reduce((acc, state) => {
      let historyLength = 0;
      try {
        const history = state.position_history || "[]";
        let parsedHistory;
        if (typeof history !== "string") {
          parsedHistory = Array.isArray(history) ? history : [];
        } else {
          parsedHistory = JSON.parse(history);
        }
        historyLength = Array.isArray(parsedHistory) ? parsedHistory.length : 0;
      } catch (e) {
        historyLength = 0;
      }
      const turnsForTeam = Math.max(0, historyLength - 1);
      return acc + turnsForTeam;
    }, 0);

    const revertsUsed = teamStates.reduce(
      (acc, state) => acc + (state.revert_count || 0),
      0
    );
    const totalRevertsAllowed =
      (gameSettings.max_reverts_allowed || 0) * teamStates.length;

    return (
      <div className="game-management-view">
        <h4>
          <BarChart2 size={18} style={{ marginRight: "5px" }} /> Live Game
          Tracker / Results
        </h4>
        <GameStatusControls
          status={gameSettings.status}
          onAction={onGameAction}
          onReset={onResetAction}
          clanId={gameSettings.clan_id}
          eventId={gameSettings.event_id}
          gameId={gameSettings.id}
        />
        <div className="game-status-info">
          <p>
            <strong>Total Turns Processed:</strong> {totalTurns} turns
          </p>
          <p>
            <strong>Reverts Used:</strong> {revertsUsed} / {totalRevertsAllowed}{" "}
            Total
          </p>
        </div>
        <h5 style={{ marginTop: "20px" }}>Team Positions</h5>
        <ul className="team-positions-list">
          {teamStates.map((state) => (
            <li
              key={state.team_id}
              className="team-position-item"
              style={{ 
                borderLeft: `5px solid ${state.team_color || "#333"}`,
                color: state.team_color || "#333" 
              }}
            >
              <div className="team-info">
                <span
                  style={{
                    color: state.team_color || "#333",
                    fontWeight: "bold",
                  }}
                >
                  {state.team_name}:
                </span>
                <span className="current-pos">
                  Position: {state.current_position}
                </span>
              </div>
              <div className="admin-team-actions">
                <button
                  className="btn-admin-action btn-set"
                  onClick={() => onOpenSetPositionModal(state)}
                  title="Set Team Position"
                >
                  <Settings size={14} />
                </button>
                <button
                  className="btn-admin-action btn-revert"
                  onClick={() => onOpenRevertModal(state)}
                  title="Revert Last Move"
                >
                  <RotateCcw size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

function ClanEventGameTab({ clanId, event, goBackToList }) {
  const [gameSettings, setGameSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: null, text: null });
  const [isMessageFading, setIsMessageFading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showRandomizeConfirmModal, setShowRandomizeConfirmModal] =
    useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const [showRevertConfirmModal, setShowRevertConfirmModal] = useState(false);
  const [showSetPositionModal, setShowSetPositionModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newPositionInput, setNewPositionInput] = useState("");
  const [adminActionError, setAdminActionError] = useState(null);
  const [adminActionReason, setAdminActionReason] = useState("");

  const socketRef = useRef(null);

  const handleApiMessage = useCallback((type, text) => {
    setApiMessage({ type, text });
  }, []);

  const clearMessage = () => {
    setApiMessage({ type: null, text: null });
    setIsMessageFading(false);
  };

  useEffect(() => {
    let fadeTimer;
    let clearTimer;
    if (apiMessage.text) {
      if (!formError) {
        fadeTimer = setTimeout(() => {
          setIsMessageFading(true);
        }, MESSAGE_TIMEOUT_MS - 1000);
        clearTimer = setTimeout(clearMessage, MESSAGE_TIMEOUT_MS);
      }
    }
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
      if (apiMessage.text) {
        setIsMessageFading(false);
      }
    };
  }, [apiMessage.text, formError]);

  const fetchGameData = useCallback(async () => {
    if (!event.game_id) {
      setLoading(false);
      setGameSettings(null);
      setFormData(initialFormData);
      return;
    }
    setLoading(true);
    setApiMessage({ type: null, text: null });
    try {
      const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${event.game_id}`;
      const response = await fetch(url, { credentials: "include" });
      const data = await response.json();
      if (!response.ok) {
        handleApiMessage("error", data.error || "Failed to fetch game data.");
        setGameSettings(null);
      } else {
        const gameData = data.game;
        setGameSettings(gameData);

        const fallbackDist = calculateTaskDistribution(
          gameData.board_size,
          gameData.num_snakes,
          gameData.num_ladders,
        );

        setFormData({
          board_size: gameData.board_size,
          num_snakes: String(gameData.num_snakes),
          num_ladders: String(gameData.num_ladders),
          max_reverts_allowed: String(gameData.max_reverts_allowed),
          num_easy_tasks: String(
            gameData.num_easy_tasks !== undefined &&
              gameData.num_easy_tasks !== null
              ? gameData.num_easy_tasks
              : fallbackDist.num_easy_tasks,
          ),
          num_medium_tasks: String(
            gameData.num_medium_tasks !== undefined &&
              gameData.num_medium_tasks !== null
              ? gameData.num_medium_tasks
              : fallbackDist.num_medium_tasks,
          ),
          num_hard_tasks: String(
            gameData.num_hard_tasks !== undefined &&
              gameData.num_hard_tasks !== null
              ? gameData.num_hard_tasks
              : fallbackDist.num_hard_tasks,
          ),
          num_elite_tasks: String(
            gameData.num_elite_tasks !== undefined &&
              gameData.num_elite_tasks !== null
              ? gameData.num_elite_tasks
              : fallbackDist.num_elite_tasks,
          ),
          num_master_tasks: String(
            gameData.num_master_tasks !== undefined &&
              gameData.num_master_tasks !== null
              ? gameData.num_master_tasks
              : fallbackDist.num_master_tasks,
          ),
        });
      }
    } catch (e) {
      handleApiMessage("error", "Network error fetching game data.");
      console.error("Fetch Game Data Error:", e);
    } finally {
      setLoading(false);
    }
  }, [clanId, event.id, event.game_id, handleApiMessage]);

  useEffect(() => {
    if (clanId && event.id) {
      fetchGameData();
    }
  }, [clanId, event.id, fetchGameData]);

  // 🔑 NEW: useEffect for Socket.IO connection and event listeners
  useEffect(() => {
    const gameId = event.game_id; // Get the gameId from the event prop
    if (!gameId) {
      // If there's no game ID, ensure any existing socket is disconnected
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Connect only if not already connected or if gameId changes
    if (!socketRef.current) {
      const newSocket = io(process.env.REACT_APP_API_BASE_URL, {
        withCredentials: true,
      });
      socketRef.current = newSocket;

      newSocket.on("connect", () => {
        console.log(`Socket connected for game ${gameId}`);
        newSocket.emit("join_game", { game_id: gameId }); // Use the event your GameDetail uses
      });

      // Handler similar to GameDetail's, focusing on updating gameSettings
      const handleGameStateUpdate = (data) => {
        // Check if the update is for the currently viewed game
        // This check assumes data.game_state.id exists, adjust if needed
        if (data.game_state && data.game_state.id === gameId) {
          setGameSettings((prevSettings) => {
            if (!prevSettings) return data.game_state; // Initialize if null

            const incomingGame = data.game_state;

            // Merge incoming game state, ensuring team_states are updated
            const updatedSettings = {
              ...prevSettings,
              ...incomingGame, // Apply all updates from the server
              // Explicitly handle team_states to ensure update
              team_states:
                incomingGame.team_states || prevSettings.team_states || [],
              status: incomingGame.status || prevSettings.status, // Ensure status updates
            };

            return updatedSettings;
          });

          // Clear loading states, as the socket push confirms the action is complete
          setLoading(false);
        }
      };

      // Handler for partial updates (like admin revert) that trigger a refetch
      const handlePartialGameUpdate = (data) => {
        if (data.game_id === gameId) {
          // This update type doesn't send the full state,
          // so we trigger a manual refetch.
          fetchGameData();
        }
      };

      newSocket.on("game_state_update", handleGameStateUpdate);
      newSocket.on("tasks_reordered", handleGameStateUpdate);
      newSocket.on("game_update", handlePartialGameUpdate); // 🔑 ADDED

      newSocket.on("disconnect", () => {
        console.log(`Socket disconnected for game ${gameId}`);
      });

      newSocket.on("connect_error", (err) => {
        console.error("Socket connection error:", err);
        handleApiMessage(
          "error",
          "Socket connection error. Real-time updates might fail."
        );
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.emit("leave_game", { game_id: gameId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [event.game_id, fetchGameData, handleApiMessage]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "board_size") {
      newValue = parseInt(value, 10);
    }

    setFormData((prev) => {
      let nextData = { ...prev, [name]: newValue };
      setFormError(null);
      clearMessage();

      const triggerFields = ["board_size", "num_snakes", "num_ladders"];
      if (triggerFields.includes(name)) {
        const bSize = parseInt(nextData.board_size, 10);
        const sCount = parseInt(nextData.num_snakes || "0", 10);
        const lCount = parseInt(nextData.num_ladders || "0", 10);

        if (PERFECT_SQUARE_SIZES.includes(bSize)) {
          const distribution = calculateTaskDistribution(bSize, sCount, lCount);
          return {
            ...nextData,
            num_easy_tasks: String(distribution.num_easy_tasks),
            num_medium_tasks: String(distribution.num_medium_tasks),
            num_hard_tasks: String(distribution.num_hard_tasks),
            num_elite_tasks: String(distribution.num_elite_tasks),
            num_master_tasks: String(distribution.num_master_tasks),
          };
        }
      }
      return nextData;
    });
  };

  const validateFormData = () => {
    if (!PERFECT_SQUARE_SIZES.includes(formData.board_size)) {
      return "Board size must be a perfect square between 25 and 225.";
    }

    const num_snakes = parseInt(formData.num_snakes || "0", 10);
    const num_ladders = parseInt(formData.num_ladders || "0", 10);
    const max_reverts_allowed = parseInt(
      formData.max_reverts_allowed || "0",
      10
    );

    if (num_snakes < 0 || num_snakes > 12)
      return "Number of snakes must be between 0 and 12.";
    if (num_ladders < 0 || num_ladders > 12)
      return "Number of ladders must be between 0 and 12.";
    if (max_reverts_allowed < 0 || max_reverts_allowed > 10)
      return "Max reverts allowed must be between 0 and 10.";

    const taskSumError = validateTaskSum(formData);
    if (taskSumError) {
      return taskSumError;
    }

    return null;
  };

  const getApiPayload = () => {
    return {
      board_size: formData.board_size,
      num_snakes: parseInt(formData.num_snakes || "0", 10),
      num_ladders: parseInt(formData.num_ladders || "0", 10),
      max_reverts_allowed: parseInt(formData.max_reverts_allowed || "0", 10),
      num_easy_tasks: parseInt(formData.num_easy_tasks || "0", 10),
      num_medium_tasks: parseInt(formData.num_medium_tasks || "0", 10),
      num_hard_tasks: parseInt(formData.num_hard_tasks || "0", 10),
      num_elite_tasks: parseInt(formData.num_elite_tasks || "0", 10),
      num_master_tasks: parseInt(formData.num_master_tasks || "0", 10),
    };
  };

  const handleGameSubmitApiCall = async (mode) => {
    const isCreate = mode === "create";
    const method = isCreate ? "POST" : "PUT";
    const url = isCreate
      ? `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/`
      : `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameSettings.id}`;

    const payload = getApiPayload();

    setLoading(true);
    setFormError(null);
    clearMessage();

    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        const message =
          data.error ||
          `Failed to ${isCreate ? "create" : "randomize/update"} game.`;
        handleApiMessage("error", message);
        setFormError(message);
      } else {
        const successMessage = isCreate
          ? `Game created successfully! Snakes and ladders board is ready.`
          : `Board re-randomized successfully using the current settings.`;
        handleApiMessage("success", successMessage);
        if (isCreate) {
          event.game_id = data.game.id;
        }
        await fetchGameData();
      }
    } catch (e) {
      setFormError("Network error. Failed to connect to the server.");
      console.error(`Game ${mode} network error:`, e);
    } finally {
      setLoading(false);
    }
  };

  const handleGameSubmit = async (mode) => {
    if (loading) return;

    const validationError = validateFormData();
    if (validationError) {
      setFormError(validationError);
      handleApiMessage("error", validationError); // Show validation error in main message area
      return;
    }

    // This logic seems incorrect, handleGameSubmitApiCall should be called directly
    // Let's fix this to call the API call logic
    // setLoading(true); // setLoading is handled inside handleGameSubmitApiCall
    // setFormError(null);
    // clearMessage();

    const isUpdate = mode === "update";
    const status = gameSettings?.status;
    if (
      isUpdate &&
      status &&
      (status === "in-progress" || status === "paused")
    ) {
      // If updating an active game, show the randomize modal
      setShowRandomizeConfirmModal(true);
      return;
    }
    // If creating or updating a non-active game, proceed directly
    await handleGameSubmitApiCall(mode);
  };

  const handleConfirmRandomize = async () => {
    setShowRandomizeConfirmModal(false);
    setLoading(true);
    // This function is now only for randomizing an existing, active game's settings
    await handleGameSubmitApiCall("update");
  };

  const handleDeleteGame = async () => {
    if (loading || !gameSettings) return;
    setLoading(true);
    clearMessage();
    setShowDeleteModal(false);
    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameSettings.id}`;
    try {
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const data = await response.json();
        handleApiMessage("error", data.error || "Failed to delete game.");
      } else {
        handleApiMessage(
          "success",
          `Game for '${event.name}' deleted successfully. The event is now awaiting a new game setup.`
        );
        event.game_id = null;
        setGameSettings(null);
        setFormData(initialFormData);
      }
    } catch (e) {
      handleApiMessage(
        "error",
        "Network error. Failed to connect to the server."
      );
      console.error("Delete Game network error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleResetGame = async () => {
    if (loading || !gameSettings) return;
    setLoading(true);
    clearMessage();
    setShowResetModal(false);
    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameSettings.id}/reset`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        handleApiMessage("error", data.error || "Failed to reset game.");
      } else {
        handleApiMessage(
          "success",
          `Game for '${event.name}' has been successfully reset. All team positions and histories have been cleared.`
        );
        await fetchGameData();
      }
    } catch (e) {
      handleApiMessage(
        "error",
        "Network error. Failed to connect to the server."
      );
      console.error("Reset Game network error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Handlers to OPEN the admin modals
  const handleOpenRevertModal = (teamState) => {
    setSelectedTeam(teamState);
    setShowRevertConfirmModal(true);
    setAdminActionError(null);
    setAdminActionReason("");
  };

  const handleOpenSetPositionModal = (teamState) => {
    setSelectedTeam(teamState);
    setNewPositionInput(String(teamState.current_position || "1"));
    setShowSetPositionModal(true);
    setAdminActionError(null);
    setAdminActionReason("");
  };

  // Handler to CONFIRM and call API for Revert
  const handleConfirmRevertTeam = async () => {
    // Add reason validation
    if (adminActionReason.trim() === "") {
      setAdminActionError("A reason is required for this action.");
      return;
    }
    if (!selectedTeam || !gameSettings) return;

    setLoading(true);
    setAdminActionError(null);
    clearMessage();
    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/admin/team-revert`; // Your new route
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          game_id: gameSettings.id,
          team_id: selectedTeam.team_id,
          reason: adminActionReason,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setAdminActionError(data.error || "Failed to revert team position.");
        handleApiMessage(
          "error",
          data.error || "Failed to revert team position."
        );
      } else {
        handleApiMessage(
          "success",
          `Team ${selectedTeam.team_name} position reverted.`
        );
        setShowRevertConfirmModal(false);
        setSelectedTeam(null);
        setAdminActionReason("");
        // Socket update will handle the state change
      }
    } catch (e) {
      setAdminActionError("Network error. Could not revert position.");
      handleApiMessage("error", "Network error. Could not revert position.");
    } finally {
      setLoading(false);
    }
  };

  // Handler to CONFIRM and call API for Set Position
  const handleConfirmSetPosition = async () => {
    if (!selectedTeam || !gameSettings) return;

    // Add reason validation
    if (adminActionReason.trim() === "") {
      setAdminActionError("A reason is required for this action.");
      return;
    }

    const newPos = parseInt(newPositionInput, 10);
    if (isNaN(newPos) || newPos < 1 || newPos > gameSettings.board_size) {
      setAdminActionError(
        `Position must be a number between 1 and ${gameSettings.board_size}.`
      );
      return;
    }

    setLoading(true);
    setAdminActionError(null);
    clearMessage();

    const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/admin/team-set-position`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          game_id: gameSettings.id,
          team_id: selectedTeam.team_id,
          new_position: newPos,
          reason: adminActionReason,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setAdminActionError(data.error || "Failed to set team position.");
        handleApiMessage("error", data.error || "Failed to set team position.");
      } else {
        handleApiMessage(
          "success",
          `Team ${selectedTeam.team_name} position set to ${newPos}.`
        );
        setShowSetPositionModal(false);
        setSelectedTeam(null);
        setNewPositionInput("");
        setAdminActionReason("");
        // Socket update will handle the state change
      }
    } catch (e) {
      setAdminActionError("Network error. Could not set position.");
      handleApiMessage("error", "Network error. Could not set position.");
    } finally {
      setLoading(false);
    }
  };

  const handleBoardConfigUpdate = useCallback(
    async (configPayload) => {
      if (!gameSettings) throw new Error("Game is not initialized.");
      const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameSettings.id}/board`;
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(configPayload),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Server failed to process board update.");
      }
      await fetchGameData();
    },
    [gameSettings, clanId, event.id, fetchGameData]
  );

  const handleGameManagementAction = useCallback(
    async (action, setLocalLoading) => {
      if (!gameSettings) {
        setLocalLoading(false);
        return;
      }
      clearMessage();
      let newStatus = null;
      let successMessage = "";
      if (action === "pause") {
        newStatus = "paused";
        successMessage = "Game paused successfully. No turns can be processed.";
      } else if (action === "resume") {
        newStatus = "in-progress";
        successMessage = "Game resumed. Turns can now be processed.";
      } else {
        setLocalLoading(false);
        handleApiMessage("error", `Unknown game action: ${action}`);
        return;
      }
      const url = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameSettings.id}/status`;
      try {
        const response = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
          credentials: "include",
        });
        const data = await response.json();
        if (!response.ok) {
          handleApiMessage("error", data.error || `Failed to ${action} game.`);
        } else {
          handleApiMessage("success", successMessage);
          // Rely on socket update to reflect status change
          // setGameSettings((prevSettings) => ({ ...prevSettings, status: newStatus }));
        }
      } catch (e) {
        handleApiMessage("error", "Network error managing game status.");
        console.error(`Game ${action} network error:`, e);
      } finally {
        setLocalLoading(false); // Reset button loading state via callback
      }
    },
    [gameSettings, clanId, event.id, handleApiMessage, clearMessage]
  );

  const handleResetAction = useCallback(() => {
    setShowResetModal(true);
  }, []);

  const renderGameSetupForm = (isExistingGame) => {
    const taskSumError = validateTaskSum(formData);
    const isDisabled = loading || !!taskSumError || !!formError;

    const submitText = isExistingGame
      ? loading
        ? "Saving..." // Changed text for clarity
        : "Save & Randomize Board"
      : loading
      ? "Creating..."
      : "Create Game";
    const submitButtonClass = isDisabled
      ? "game-submit-btn btn-disabled"
      : "game-submit-btn";

    const board_size = parseInt(formData.board_size, 10);
    const num_snakes = parseInt(formData.num_snakes || "0", 10);
    const num_ladders = parseInt(formData.num_ladders || "0", 10);

    const totalTiles =
      board_size > 0 ? board_size - 2 - num_snakes - num_ladders : 0;

    return (
      <div className="game-settings-form-container">
        {(formError || taskSumError) && (
          <div className="game-global-message error form-error-message">
            {formError || taskSumError}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleGameSubmit(isExistingGame ? "update" : "create");
          }}
        >
          <div className="form-field-grid">
            <div>
              <h5 style={{ marginBottom: "15px" }}>Board Rules</h5>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="board_size">
                  Board Size (Perfect Square: 25 to 225)
                </label>
                <select
                  id="board_size"
                  className="modal-form-field"
                  name="board_size"
                  value={formData.board_size}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                >
                  {PERFECT_SQUARE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} ({Math.sqrt(size)}x{Math.sqrt(size)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_snakes">
                  Num Snakes (Max 12)
                </label>
                <input
                  id="num_snakes"
                  className="modal-form-field"
                  type="number"
                  name="num_snakes"
                  min="0"
                  max="12"
                  value={formData.num_snakes || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_ladders">
                  Num Ladders (Max 12)
                </label>
                <input
                  id="num_ladders"
                  className="modal-form-field"
                  type="number"
                  name="num_ladders"
                  min="0"
                  max="12"
                  value={formData.num_ladders || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label
                  className="modal-form-label"
                  htmlFor="max_reverts_allowed"
                >
                  Max Reverts Allowed per Team
                </label>
                <input
                  id="max_reverts_allowed"
                  className="modal-form-field"
                  type="number"
                  name="max_reverts_allowed"
                  min="0"
                  max="10"
                  value={formData.max_reverts_allowed || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
            </div>
            <div>
              <h5 style={{ marginBottom: "15px" }}>Task Distribution</h5>
              <p
                className={`note-text-config ${taskSumError ? "error-text" : ""}`}
              >
                Available task tiles: <strong>{Math.max(0, totalTiles)}</strong>{" "}
                (Excludes Start, End, and {num_snakes + num_ladders} connection
                points)
              </p>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_easy_tasks">
                  Num Easy Tasks
                </label>
                <input
                  id="num_easy_tasks"
                  className="modal-form-field"
                  type="number"
                  name="num_easy_tasks"
                  min="0"
                  value={formData.num_easy_tasks || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_medium_tasks">
                  Num Medium Tasks
                </label>
                <input
                  id="num_medium_tasks"
                  className="modal-form-field"
                  type="number"
                  name="num_medium_tasks"
                  min="0"
                  value={formData.num_medium_tasks || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_hard_tasks">
                  Num Hard Tasks
                </label>
                <input
                  id="num_hard_tasks"
                  className="modal-form-field"
                  type="number"
                  name="num_hard_tasks"
                  min="0"
                  value={formData.num_hard_tasks || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_elite_tasks">
                  Num Elite Tasks
                </label>
                <input
                  id="num_elite_tasks"
                  className="modal-form-field"
                  type="number"
                  name="num_elite_tasks"
                  min="0"
                  value={formData.num_elite_tasks || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
              <div className="form-group">
                <label className="modal-form-label" htmlFor="num_master_tasks">
                  Num Master Tasks
                </label>
                <input
                  id="num_master_tasks"
                  className="modal-form-field"
                  type="number"
                  name="num_master_tasks"
                  min="0"
                  value={formData.num_master_tasks || ""}
                  onChange={handleFormChange}
                  disabled={loading}
                  required
                />
              </div>
            </div>
          </div>
          <div className="form-submit-row">
            <p className="note-text">
              * Submitting will{" "}
              {isExistingGame ? "update the saved settings" : "create the game"}
              . Board randomization happens separately.
            </p>
            <button
              type="submit"
              className={`btn-primary ${submitButtonClass}`}
              disabled={isDisabled}
            >
              <Settings size={16} style={{ marginRight: "5px" }} />
              {submitText}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderGameSetupAndConfig = () => {
    if (!gameSettings) return null;
    return (
      <>
        <div className="game-board-config-area">
          <BoardConfigEditor
            gameSettings={gameSettings}
            loading={loading}
            onUpdate={handleBoardConfigUpdate}
            onApiMessage={handleApiMessage}
          />
          <hr className="section-divider" />
        </div>
        <div className="game-setup-area">
          <h4 style={{ marginBottom: "15px" }}>
            <Dice5 size={18} style={{ marginRight: "5px" }} /> Board Settings
          </h4>
          {renderGameSetupForm(true)}
          <div className="delete-game-container">
            <button
              className="btn-danger btn-small delete-game-btn"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              <Trash2 size={16} style={{ marginRight: "5px" }} />
              Delete Game (Wipe Board)
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderGameArea = () => {
    if (loading && event.game_id && !gameSettings) {
      // Only show loading if actively fetching initial data
      return (
        <div className="game-status-area loading">
          <p className="loading-message">
            <Zap size={16} className="spin-icon" />
            {"Loading game details..."}
          </p>
        </div>
      );
    }
    if (loading && !gameSettings) {
      // 🔑 FIXED: Adjusted loading text condition
      return (
        <div className="game-status-area loading">
          <p className="loading-message">
            <Zap size={16} className="spin-icon" />
            &nbsp;{"Deploying game, please wait..."}
          </p>
        </div>
      );
    }
    if (gameSettings) {
      return (
        <div className="game-view-sections">
          <div className="game-results-area">
            <GameManagementView
              gameSettings={gameSettings}
              onGameAction={handleGameManagementAction}
              onResetAction={handleResetAction}
              // 🔑 NEW: Pass handlers to the view
              onOpenRevertModal={handleOpenRevertModal}
              onOpenSetPositionModal={handleOpenSetPositionModal}
            />
          </div>
          {renderGameSetupAndConfig()}
        </div>
      );
    } else {
      return (
        <div className="game-setup-area">
          <h2>
            <Play size={20} style={{ marginRight: "8px" }} /> Setup New Game
          </h2>
          <p className="sub-view-intro">
            The Snakes and Ladders board has not been created for this event
            yet. Set the initial parameters below and click "Create & Deploy
            Game".
          </p>
          {renderGameSetupForm(false)}
        </div>
      );
    }
  };

  return (
    <div className="clan-event-game-tab-container">
      <div className="event-sub-nav">
        <button
          className="sub-nav-button"
          onClick={goBackToList}
          disabled={loading}
        >
          <ChevronLeft size={18} style={{ marginRight: "5px" }} />
          Back to Events
        </button>
        <span className="current-context-display">
          Managing:&nbsp;
          <strong className="current-view-name">{"Game Board"}</strong>
        </span>
      </div>
      <div className="game-tab-content-area">
        <div className="message-container">
          {apiMessage.text && (
            <div
              className={`game-global-message ${apiMessage.type} ${
                isMessageFading ? "fading-out" : ""
              }`}
            >
              {apiMessage.text}
            </div>
          )}
        </div>
        {renderGameArea()}
      </div>

      <Modal
        isOpen={showDeleteModal}
        title="Confirm Game Deletion"
        message={
          <div className="delete-confirm-message">
            <p>
              Are you sure you want to&nbsp;
              <strong>permanently delete</strong> the game board and all
              progress for:
            </p>
            <p className="event-name-to-delete">"{event.name}"</p>
            <p>
              This action cannot be undone. You will need to create a new
              game&nbsp;board to continue.
            </p>
          </div>
        }
        onConfirm={handleDeleteGame}
        onCancel={() => setShowDeleteModal(false)}
        confirmButtonText={loading ? "Processing..." : "Delete Game"}
        confirmButtonClassName={
          loading ? "btn-danger btn-disabled" : "btn-danger"
        }
        cancelButtonText="Cancel"
      />
      <Modal
        isOpen={showResetModal}
        title="Confirm Game Reset"
        message={
          <div className="reset-confirm-message">
            <p>
              Are you sure you want to <strong>reset</strong> the game progress
              for "{event.name}"?
            </p>
            <p>
              This will&nbsp;
              <strong>
                clear all team positions, move history, and revert counts
              </strong>
              . The current board configuration (snakes, ladders, size) will be
              kept.
            </p>
            <p>The game status will be set back to 'READY'.</p>
          </div>
        }
        onConfirm={handleResetGame}
        onCancel={() => setShowResetModal(false)}
        confirmButtonText={loading ? "Processing..." : "Reset Game"}
        confirmButtonClassName={
          loading ? "btn-danger-action btn-disabled" : "btn-danger-action"
        }
        cancelButtonText="Cancel"
      />
      <Modal
        isOpen={showRandomizeConfirmModal}
        title="Confirm Board Randomization"
        message={
          <div className="randomize-confirm-message">
            <p>
              The game for <strong>{event.name}</strong> is currently
              <strong> {gameSettings?.status.toUpperCase()}</strong>.
            </p>
            <p>
              <strong>WARNING:</strong> Randomizing the board will&nbsp;
              <strong>permanently change</strong> the location of all existing
              snakes and ladders. This action is
              <strong> NOT</strong> a game reset.
            </p>
            <p>
              The teams'&nbsp;
              <strong>current positions will remain unchanged</strong>, but the
              paths on the board will be completely different.
            </p>
            <p>
              <strong>
                Are you sure you want to re-randomize the board now?
              </strong>
            </p>
          </div>
        }
        onConfirm={handleConfirmRandomize}
        onCancel={() => {
          setShowRandomizeConfirmModal(false);
          setLoading(false);
        }}
        confirmButtonText="Yes, Randomize Board"
        confirmButtonClassName="btn-warning"
        cancelButtonText="No, Keep Current Board"
      />

      {/* Modal for Admin Revert */}
      <Modal
        isOpen={showRevertConfirmModal}
        title="Confirm Admin Revert"
        message={
          <div className="admin-action-confirm-message">
            {adminActionError && (
              <div className="modal-error-message">{adminActionError}</div>
            )}
            <p>Are you sure you want to revert the last move for team:</p>
            <p
              className="event-name-to-delete"
              style={{ color: selectedTeam?.team_color || "#333" }}
            >
              "{selectedTeam?.team_name}"
            </p>
            <p>
              This will move them from position{" "}
              <strong>{selectedTeam?.current_position}</strong> back to their
              previous position.
            </p>
            {/* Added Reason field */}
            <div className="form-group" style={{ marginTop: "15px" }}>
              <label className="modal-form-label" htmlFor="admin_revert_reason">
                Reason (Required)
              </label>
              <textarea
                id="admin_revert_reason"
                className="modal-form-field modal-textarea"
                rows="3"
                value={adminActionReason}
                onChange={(e) => setAdminActionReason(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        }
        onConfirm={handleConfirmRevertTeam}
        onCancel={() => setShowRevertConfirmModal(false)}
        confirmButtonText={loading ? "Processing..." : "Confirm Revert"}
        confirmButtonClassName={
          loading ? "btn-danger-action btn-disabled" : "btn-danger-action"
        }
        cancelButtonText="Cancel"
      />

      {/* Modal for Admin Set Position */}
      <Modal
        isOpen={showSetPositionModal}
        title="Admin Set Position"
        message={
          <div className="admin-action-set-position">
            {adminActionError && (
              <div className="modal-error-message">{adminActionError}</div>
            )}
            <p>
              Set the position for team:{" "}
              <strong style={{ color: selectedTeam?.team_color || "#333" }}>
                {selectedTeam?.team_name}
              </strong>
            </p>
            <div className="form-group" style={{ marginTop: "15px" }}>
              <label className="modal-form-label" htmlFor="new_position_input">
                New Position (1 - {gameSettings?.board_size || 100})
              </label>
              <input
                id="new_position_input"
                className="modal-form-field"
                type="number"
                name="new_position_input"
                min="1"
                max={gameSettings?.board_size || 100}
                value={newPositionInput}
                onChange={(e) => setNewPositionInput(e.target.value)}
                disabled={loading}
              />
            </div>
            {/* Added Reason field */}
            <div className="form-group" style={{ marginTop: "15px" }}>
              <label
                className="modal-form-label"
                htmlFor="admin_set_pos_reason"
              >
                Reason (Required)
              </label>
              <textarea
                id="admin_set_pos_reason"
                className="modal-form-field modal-textarea"
                rows="3"
                value={adminActionReason}
                onChange={(e) => setAdminActionReason(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        }
        onConfirm={handleConfirmSetPosition}
        onCancel={() => setShowSetPositionModal(false)}
        confirmButtonText={loading ? "Processing..." : "Set Position"}
        confirmButtonClassName={
          loading ? "btn-primary btn-disabled" : "btn-primary"
        }
        cancelButtonText="Cancel"
      />
    </div>
  );
}

export default ClanEventGameTab;
